/* global Excel */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// ── Workbook helpers (unchanged) ─────────────────────────────────────────────

async function getActiveSheetName() {
  return Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    await ctx.sync();
    return sheet.name;
  });
}

// Visible sheets only: hidden helper sheets are never meant for a client PDF.
async function getAllSheetNames() {
  return Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load("items/name,items/visibility");
    await ctx.sync();
    return sheets.items.filter((s) => s.visibility === "Visible").map((s) => s.name);
  });
}

export async function listWorkbookSheets() {
  const [activeName, allNames] = await Promise.all([
    getActiveSheetName(),
    getAllSheetNames(),
  ]);
  const recommendedNames = allNames.filter(
    (n) => n === activeName || n.startsWith(activeName + "-")
  );
  return { activeName, allNames, recommendedNames };
}

const PDF_FONT_SIZES = [7, 8, 9, 10];
const safeFileName = (name) =>
  String(name || "")
    .replace(/\.pdf$/i, "")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .trim()
    .slice(0, 120);

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Fetch the Georgian font once per session; later exports reuse it.
let sylfaenBase64Promise = null;
function loadSylfaenBase64() {
  if (!sylfaenBase64Promise) {
    sylfaenBase64Promise = fetch("/assets/fonts/sylfaen.ttf")
      .then(async (response) => {
        if (!response.ok) throw new Error("Could not load font: " + response.status);
        return arrayBufferToBase64(await response.arrayBuffer());
      })
      .catch((err) => {
        sylfaenBase64Promise = null; // allow a retry on the next export
        throw err;
      });
  }
  return sylfaenBase64Promise;
}

async function registerUnicodeFont(doc) {
  const base64 = await loadSylfaenBase64();
  doc.addFileToVFS("sylfaen.ttf", base64);
  doc.addFont("sylfaen.ttf", "Sylfaen", "normal");
}

// ── Cell formatting ───────────────────────────────────────────────────────────

/**
 * Convert an Excel cell value to a display string.
 * - numbers with decimals → locale-formatted (e.g. 235,642.19)
 * - small integers (row/column numbers) → plain digits
 * - zero → empty string (no clutter in price columns)
 */
function cellStr(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    if (!Number.isFinite(v) || v === 0) return "";
    if (Number.isInteger(v) && v > 0 && v < 10000) return String(v);
    return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(v);
}

function cap(s, maxLen) {
  if (!s) return "";
  return s.length > maxLen ? s.slice(0, maxLen - 1) + "…" : s;
}

// Summary-row labels, matching the sheet templates in all three languages.
const SUM_LABELS = new Set(["ჯამი", "total", "итого"]);
const ADJUST_WORDS = [
  "ზედნადებ", "გეგმიური", "გაუთვალისწინებელ", "დ.ღ.გ", "დღგ",
  "overhead", "accumulation", "unforeseen", "contingency", "vat",
  "ндс", "накладн", "непредвиден", "плановые",
];
const FOOTER_WORDS = ["შეასრულა", "www.boq.ge", "prepared by", "выполнил"];

/**
 * Classify a body row so the PDF can colour summary rows like the Excel sheet:
 *   "sum"    — ჯამი / Total / Итого subtotal rows
 *   "adjust" — overhead, accumulation, VAT, contingency rows
 *   "footer" — the signed-by / www.boq.ge line
 *   null     — ordinary resource or chapter row
 */
function rowKind(rawRow) {
  const cells = (rawRow || []).map((c) => String(c ?? "").trim().toLowerCase()).filter(Boolean);
  if (!cells.length) return null;
  if (cells.some((c) => FOOTER_WORDS.some((w) => c.includes(w)))) return "footer";
  if (cells.some((c) => SUM_LABELS.has(c))) return "sum";
  if (cells.some((c) => ADJUST_WORDS.some((w) => c.includes(w)))) return "adjust";
  return null;
}

function rowIsEmpty(row) {
  return !row || row.every((c) => c === null || c === undefined || c === "" || c === 0);
}

/** Looks like "1  2  3  4  ..." — a column-number labelling row, not data. */
function isColumnNumberRow(row) {
  const cells = (row || []).map((c) => cellStr(c).trim()).filter(Boolean);
  if (cells.length < 3) return false;
  return cells.every((c) => /^\d{1,3}$/.test(c));
}

// ── Sheet parsing ─────────────────────────────────────────────────────────────

/**
 * Detect a child BOQ sheet (13-column, custom-boq-table.js format).
 * Row index 3 (Excel row 4) contains sequential column numbers "1","2","3",...
 */
function isChildBoqSheet(allValues) {
  const r3 = (allValues[3] || []).slice(0, 6).map((v) => String(v ?? "").trim());
  return r3[0] === "1" && r3[1] === "2" && r3[2] === "3" && r3[3] === "4";
}

/**
 * Parse a child BOQ sheet.
 * Rows 0–2 have 3-level merged headers; data starts at row index 8.
 * The header is reconstructed by merging labels from all 3 header rows.
 */
function parseChildBoqSheet(allValues) {
  const r0 = allValues[0] || [];
  const r1 = allValues[1] || [];
  const r2 = allValues[2] || [];
  const ncols = Math.max(r0.length, r1.length, r2.length, 13);

  // For merged-cell columns (r0 is empty), find the nearest parent label to the left
  const header = Array.from({ length: ncols }, (_, ci) => {
    const v0 = cellStr(r0[ci]).trim();
    const v1 = cellStr(r1[ci]).trim();
    const v2 = cellStr(r2[ci]).trim();
    if (v0) {
      const sub = v2 || v1;
      return sub ? v0 + " " + sub : v0;
    }
    // Find parent label in r0 to the left
    let parent = "";
    for (let j = ci - 1; j >= 0; j--) {
      const pv = cellStr(r0[j]).trim();
      if (pv) { parent = pv; break; }
    }
    const sub = v2 || v1;
    if (parent && sub) return parent.slice(0, 5) + " " + sub;
    return sub || parent;
  });

  const body = allValues
    .slice(8)
    .filter((row) => !rowIsEmpty(row) && !isColumnNumberRow(row));

  return { header, body };
}

/**
 * Parse a generic or root-BOQ sheet.
 * Skips sparse leading rows (the merged project-info banner area in root BOQ),
 * uses the first "dense" row (≥ 30 % non-empty cells) as the column header.
 */
function parseGenericSheet(allValues) {
  if (!allValues || !allValues.length) return { header: [], body: [] };

  const density = (row) => {
    if (!row || !row.length) return 0;
    return row.filter((c) => c !== null && c !== undefined && String(c).trim() !== "").length / row.length;
  };

  let hi = 0;
  for (let i = 0; i < Math.min(20, allValues.length); i++) {
    if (density(allValues[i]) >= 0.30) { hi = i; break; }
  }

  const header = allValues[hi] || [];
  const body = allValues
    .slice(hi + 1)
    .filter((row) => !rowIsEmpty(row) && !isColumnNumberRow(row));

  return { header, body };
}

/**
 * Return the column indices that have any content across the header row
 * or the first 100 body rows. Eliminates empty "phantom" columns that
 * arise from merged cells in Excel (the merged-away cells are "").
 */
function keepColumns(header, body) {
  const maxCols = Math.max(
    header.length,
    body.length ? Math.max(...body.slice(0, 20).map((r) => (r || []).length)) : 0,
    1
  );
  const used = new Array(maxCols).fill(false);
  header.forEach((h, ci) => { if (cellStr(h).trim()) used[ci] = true; });
  body.slice(0, 100).forEach((row) =>
    (row || []).forEach((v, ci) => { if (cellStr(v).trim()) used[ci] = true; })
  );
  return used.map((u, ci) => (u ? ci : -1)).filter((ci) => ci >= 0);
}

// ── Column width & packing ────────────────────────────────────────────────────

const MM_PER_CH_8PT = 1.85; // Sylfaen at 8pt: empirical mm/character (scales with the font size)
const PAD_MM   = 4;         // cell left + right padding
const MARGIN_MM = 8;        // left/right page margin

function naturalColWidth(headerVal, rows, ci, mmPerCh) {
  const SAMPLE_CAP = 30;
  let maxLen = Math.min(cellStr(headerVal).length, SAMPLE_CAP);
  for (let r = 0; r < Math.min(rows.length, 100); r++) {
    const len = Math.min(cellStr((rows[r] || [])[ci] ?? "").length, SAMPLE_CAP);
    if (len > maxLen) maxLen = len;
  }
  return Math.min(Math.max(maxLen * mmPerCh + PAD_MM, 12), 72);
}

function packGroups(widths, pageW) {
  const groups = [];
  let group = [], used = 0;
  for (let i = 0; i < widths.length; i++) {
    if (used + widths[i] > pageW && group.length > 0) {
      groups.push(group); group = [i]; used = widths[i];
    } else {
      group.push(i); used += widths[i];
    }
  }
  if (group.length > 0) groups.push(group);
  return groups;
}

// ── Client cover page ─────────────────────────────────────────────────────────

/**
 * If `raw` is the TOTAL (root) sheet, return its cost summary; otherwise null.
 * The used range starts at column B, so B=0, C=1, G=5 (material) .. J=8 (total).
 * Summary rows are the "ჯამი" rows: the first is the direct cost, the last is
 * the grand total after overhead and VAT.
 */
function extractRootTotals(raw) {
  const isRoot = raw.slice(0, 20).some((row) => {
    const cells = (row || []).map((c) => String(c ?? "").trim()).filter(Boolean);
    return cells.length >= 5 && cells.slice(0, 5).join(",") === "1,2,3,4,5";
  });
  if (!isRoot) return null;
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const sums = raw.filter((row) => SUM_LABELS.has(String((row || [])[1] ?? "").trim().toLowerCase()));
  if (!sums.length) return null;
  const first = sums[0];
  const last = sums[sums.length - 1];
  return {
    material: num(first[5]),
    labour: num(first[6]),
    equipment: num(first[7]),
    direct: num(first[8]),
    grand: num(last[8]),
  };
}

function drawCoverPage(doc, cover, totals, today, pal) {
  const pW = doc.internal.pageSize.getWidth();
  const pH = doc.internal.pageSize.getHeight();
  const money = (n) =>
    `${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₾`;

  doc.setFillColor(...pal.brand);
  doc.rect(0, 0, pW, 46, "F");
  doc.setFont("Sylfaen", "normal");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text("ხარჯთაღრიცხვა / BILL OF QUANTITIES", 16, 18);
  doc.setFontSize(24);
  doc.text(cap(cover.projectName || "", 60), 16, 34);

  const info = [
    ["დამკვეთი", cover.customer],
    ["ობიექტის მისამართი", cover.location],
    ["ფართობი", cover.area ? `${cover.area} მ²` : ""],
    ["დოკუმენტის №", cover.docNumber],
    ["თარიღი", cover.date || today],
    ["შეადგინა", cover.author],
  ].filter(([, v]) => v);

  let y = 66;
  info.forEach(([label, value]) => {
    doc.setFontSize(9);
    doc.setTextColor(110, 120, 130);
    doc.text(label, 16, y);
    doc.setFontSize(12);
    doc.setTextColor(30, 30, 30);
    doc.text(cap(String(value), 40), 70, y);
    doc.setDrawColor(225, 230, 235);
    doc.line(16, y + 3, 150, y + 3);
    y += 12;
  });

  if (totals) {
    // Landscape: totals box on the right. Portrait: below the info lines.
    const portrait = pW < pH;
    const bx = portrait ? 16 : pW - 112;
    const by = portrait ? y + 8 : 58;
    doc.setFillColor(245, 248, 250);
    doc.setDrawColor(210, 215, 218);
    doc.roundedRect(bx, by, 96, 84, 3, 3, "FD");
    let ty = by + 12;
    [["მასალა", totals.material], ["შრომა", totals.labour], ["ტექნიკა", totals.equipment]].forEach(([l, v]) => {
      doc.setFontSize(9);
      doc.setTextColor(110, 120, 130);
      doc.text(l, bx + 8, ty);
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text(money(v), bx + 88, ty, { align: "right" });
      ty += 9;
    });
    doc.setDrawColor(210, 215, 218);
    doc.line(bx + 8, ty - 3, bx + 88, ty - 3);
    doc.setFontSize(9);
    doc.setTextColor(110, 120, 130);
    doc.text("პირდაპირი ხარჯი", bx + 8, ty + 3);
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(money(totals.direct), bx + 88, ty + 3, { align: "right" });
    doc.setFillColor(...pal.brand);
    doc.roundedRect(bx, by + 52, 96, 32, 3, 3, "F");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text("სულ (დღგ-ს ჩათვლით)", bx + 8, by + 62);
    doc.setFontSize(16);
    doc.text(money(totals.grand), bx + 88, by + 76, { align: "right" });
    const area = Number(cover.area) || 0;
    if (area > 0 && totals.grand > 0) {
      doc.setFontSize(9);
      doc.setTextColor(110, 120, 130);
      doc.text(`1 მ² ღირებულება: ${money(totals.grand / area)}`, bx + 8, by + 94);
    }
  }

  doc.setFontSize(9);
  doc.setTextColor(140, 150, 160);
  doc.text("www.boq.ge", pW / 2, pH - 10, { align: "center" });
}

// ── Table of contents ────────────────────────────────────────────────────────

const TOC_ROW_MM = 7;
const TOC_TOP_MM = 34;

const tocRowsPerPage = (doc) => Math.max(Math.floor((doc.internal.pageSize.getHeight() - TOC_TOP_MM - 14) / TOC_ROW_MM), 1);

/** entries: [{ name, page }] with final page numbers; drawn on pages firstPage, firstPage+1, ... */
function drawToc(doc, entries, firstPage, pal) {
  const pW = doc.internal.pageSize.getWidth();
  const perPage = tocRowsPerPage(doc);
  for (let start = 0, pageNo = firstPage; start < entries.length; start += perPage, pageNo++) {
    doc.setPage(pageNo);
    doc.setFillColor(...pal.brand);
    doc.rect(0, 0, pW, 22, "F");
    doc.setFont("Sylfaen", "normal");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.text("სარჩევი", 16, 14);

    let y = TOC_TOP_MM;
    entries.slice(start, start + perPage).forEach(({ name, page }) => {
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.text(cap(name, 90), 16, y);
      doc.text(String(page), pW - 16, y, { align: "right" });
      doc.setDrawColor(...pal.border);
      doc.line(16, y + 2, pW - 16, y + 2);
      y += TOC_ROW_MM;
    });
  }
}

// ── Main export ───────────────────────────────────────────────────────────────

const PALETTES = {
  color: {
    brand: [15, 118, 110],      // teal #0F766E
    head: [27, 42, 51],         // dark blue-grey for column headers
    alt: [245, 248, 250],
    border: [210, 215, 218],
    sumFill: [0, 179, 179],     // same teal as the TOTAL sheet subtotal rows
    sumText: [255, 255, 255],
    adjFill: [27, 42, 51],      // same dark as the markup / VAT rows
    adjText: [255, 255, 255],
  },
  mono: {
    brand: [70, 70, 70],
    head: [45, 45, 45],
    alt: [246, 246, 246],
    border: [200, 200, 200],
    sumFill: [215, 215, 215],
    sumText: [0, 0, 0],
    adjFill: [110, 110, 110],
    adjText: [255, 255, 255],
  },
};

/**
 * options:
 *   cover        — client cover page data ({ projectName, customer, location, area, docNumber, date, author }) or null
 *   orientation  — "landscape" (default) | "portrait"
 *   format       — "a4" (default) | "a3"
 *   fontSize     — 7 | 8 (default) | 9 | 10
 *   mono         — true: black & white / grey print-friendly colours
 *   toc          — true: contents page with page numbers after the cover
 *   pageNumbers  — false: no "sheet · page n/N" footer (default true)
 *   fileName     — name of the downloaded file without ".pdf"
 */
export async function exportSelectedSheetsToPdf(sheetNames = [], onProgress = () => {}, options = {}) {
  const selected = Array.from(
    new Set((sheetNames || []).map((s) => String(s).trim()).filter(Boolean))
  );
  if (!selected.length) throw new Error("აირჩიე მინიმუმ ერთი ფურცელი.");

  const o = { orientation: "landscape", format: "a4", fontSize: 8, mono: false, toc: false, pageNumbers: true, ...options };
  const fontSize = PDF_FONT_SIZES.includes(Number(o.fontSize)) ? Number(o.fontSize) : 8;
  const mmPerCh = (MM_PER_CH_8PT * fontSize) / 8;
  const pal = o.mono ? PALETTES.mono : PALETTES.color;

  const doc = new jsPDF({
    orientation: o.orientation === "portrait" ? "portrait" : "landscape",
    unit: "mm",
    format: o.format === "a3" ? "a3" : "a4",
  });
  await registerUnicodeFont(doc);
  doc.setFont("Sylfaen");

  const pW = doc.internal.pageSize.getWidth();
  const pH = doc.internal.pageSize.getHeight();
  const pageW = pW - 2 * MARGIN_MM; // usable table width

  const today = new Date().toLocaleDateString("ka-GE", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });

  return Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();

    let rootTotals = null;
    let isFirstPage = true;
    const pageSheet = {}; // page number (before cover/contents are added) -> sheet name

    for (let si = 0; si < selected.length; si++) {
      const sheetName = selected[si];
      onProgress(`${sheetName} (${si + 1}/${selected.length})`);
      const sheet = sheets.getItem(sheetName);
      const range = sheet.getUsedRange();
      range.load("values");
      await context.sync();

      const raw = range.values;
      if (!raw || raw.length === 0) continue;
      if (!rootTotals) rootTotals = extractRootTotals(raw);

      // 1. Parse into header + body rows
      const { header, body } = isChildBoqSheet(raw)
        ? parseChildBoqSheet(raw)
        : parseGenericSheet(raw);

      if (!body.length) continue;

      // 2. Drop columns that have no content anywhere
      const cols = keepColumns(header, body);
      if (!cols.length) continue;

      // 3. Measure natural column widths and pack into page groups
      const widths = cols.map((ci) => naturalColWidth(header[ci], body, ci, mmPerCh));
      const groups = packGroups(widths, pageW);

      for (let gi = 0; gi < groups.length; gi++) {
        const gIdxs = groups[gi]; // indices into cols / widths arrays

        if (!isFirstPage) doc.addPage();
        isFirstPage = false;

        // ── Scale widths to fill page exactly ──
        const rawSum = gIdxs.reduce((s, gi2) => s + widths[gi2], 0);
        const scale  = rawSum > 0 ? pageW / rawSum : 1;

        const columnStyles = {};
        const capLens = gIdxs.map((gi2, pos) => {
          const w = widths[gi2] * scale;
          columnStyles[pos] = { cellWidth: w };
          return Math.max(Math.floor((w - PAD_MM) / mmPerCh), 5);
        });

        // ── Build table rows ──
        const headRow  = gIdxs.map((gi2, pos) =>
          cap(cellStr(header[cols[gi2]]), capLens[pos])
        );
        const bodyRows = body.map((row) =>
          gIdxs.map((gi2, pos) =>
            cap(cellStr((row || [])[cols[gi2]] ?? ""), capLens[pos])
          )
        );

        // ── Render table ──
        autoTable(doc, {
          startY: groups.length > 1 ? 18 : 16,
          head: [headRow],
          body: bodyRows,
          styles: {
            font: "Sylfaen",
            fontSize,
            cellPadding: { top: 2.5, bottom: 2.5, left: 2.5, right: 2.5 },
            overflow: "ellipsis",
            minCellHeight: 7,
            textColor: [30, 30, 30],
            lineColor: pal.border,
            lineWidth: 0.2,
          },
          headStyles: {
            font: "Sylfaen",
            fontStyle: "normal",
            fontSize,
            fillColor: pal.head,
            textColor: [255, 255, 255],
            halign: "center",
            valign: "middle",
            lineColor: [255, 255, 255],
            lineWidth: 0.3,
            minCellHeight: 8,
          },
          alternateRowStyles: {
            fillColor: pal.alt,
          },
          columnStyles,
          tableWidth: pageW,
          margin: { left: MARGIN_MM, right: MARGIN_MM, top: 14, bottom: 10 },
          rowPageBreak: "avoid",

          // Title bar on EVERY page of the table (also continuation pages).
          didDrawPage: () => {
            pageSheet[doc.internal.getNumberOfPages()] = sheetName;
            doc.setFillColor(...pal.brand);
            doc.rect(0, 0, pW, 14, "F");
            doc.setFont("Sylfaen", "normal");
            doc.setFontSize(11);
            doc.setTextColor(255, 255, 255);
            doc.text(cap(sheetName, 70), MARGIN_MM, 9.5);
            doc.setFontSize(8);
            doc.text(today, pW - MARGIN_MM, 9.5, { align: "right" });
            if (groups.length > 1) {
              const c0 = cols[gIdxs[0]];
              const cL = cols[gIdxs[gIdxs.length - 1]];
              doc.setFontSize(7);
              doc.text(`სვ. ${c0 + 1} – ${cL + 1}  (ნაწ. ${gi + 1}/${groups.length})`, MARGIN_MM, 13);
            }
            doc.setTextColor(0, 0, 0);
          },

          // Colour summary rows like the Excel sheet: subtotals, markup/VAT rows, footer.
          willDrawCell: (data) => {
            if (data.section !== "body") return;
            const kind = rowKind(data.row.raw);
            if (!kind) return;
            const styles = data.cell.styles;
            const adjust = kind === "adjust";
            styles.fillColor = adjust ? pal.adjFill : pal.sumFill;
            styles.textColor = adjust ? pal.adjText : pal.sumText;
            styles.fontStyle = "bold";
          },
        });
      }
    }

    const contentPages = doc.getNumberOfPages();
    if (!Object.keys(pageSheet).length) throw new Error("არჩეულ ფურცლებში ამობეჭდვადი მონაცემები ვერ მოიძებნა.");

    // Front matter: client cover page, then the contents.
    const tocEntries = [];
    if (o.toc) {
      for (let p = 1; p <= contentPages; p++) {
        if (pageSheet[p] && !tocEntries.some((e) => e.name === pageSheet[p])) tocEntries.push({ name: pageSheet[p], page: p });
      }
    }
    const tocPages = o.toc && tocEntries.length ? Math.ceil(tocEntries.length / tocRowsPerPage(doc)) : 0;
    const coverPages = options.cover ? 1 : 0;
    const front = coverPages + tocPages;
    for (let i = 0; i < front; i++) doc.insertPage(i + 1);
    if (coverPages) {
      doc.setPage(1);
      drawCoverPage(doc, options.cover, rootTotals, today, pal);
    }
    if (tocPages) {
      drawToc(doc, tocEntries.map((e) => ({ name: e.name, page: e.page + front })), coverPages + 1, pal);
    }

    // Footer on the content pages: "sheet · page n / total" (numbers include cover and contents).
    const total = doc.getNumberOfPages();
    if (o.pageNumbers) {
      for (let p = front + 1; p <= total; p++) {
        doc.setPage(p);
        doc.setFontSize(7);
        doc.setFont("Sylfaen", "normal");
        doc.setTextColor(150, 150, 150);
        doc.text(`${pageSheet[p - front] || ""}   ·   გვ. ${p} / ${total}`, pW / 2, pH - 4, { align: "center" });
        doc.setTextColor(0, 0, 0);
      }
    }

    const fileName = `${safeFileName(o.fileName) || `BOQ_${new Date().toISOString().split("T")[0]}`}.pdf`;
    doc.save(fileName);
    return { selectedCount: selected.length, exportedSheets: selected, pages: total, fileName };
  });
}
